import { Variant, Variants } from '../types/variant';

/**
 * Base générique du questionnaire pré-opératoire d'anesthésie : l'anesthésiste
 * et le praticien opérateur sont saisis en texte libre
 * (#ANS_ANESTHESISTE_LIBRE / #ANS_PRATICIEN_LIBRE dans KB-ADMIN.mdkg).
 * `anesthesie_preop_victoire`, plus bas, rallume à la place les listes
 * nominatives de l'établissement.
 */
const anesthesiePreop: Variant = {
  name: 'Anesthésie pré-opératoire',
  status: 'stable',
  description:
    "Questionnaire pré-opératoire d'anesthésie couvrant les informations administratives, le traitement habituel, les antécédents, les antécédents médicaux par appareil, une section femmes, et l'état bucco-dentaire. L'anesthésiste et le praticien opérateur sont saisis en texte libre.",
  speciality: 'ANESTHESIE-PREOP',
  patientOnboarding: 'DEFAULT',
  patientOffboarding: 'DIRECT_CONFIRMATION',
  emailTemplate: 'MED_G_DEFAULT',
  reportTemplate: 'ANESTHESIA',
  initialQuestions: [],
  initialAssertions: [
    "[Message: #‹]-(06.a_lieu_emission)->[Lieu]-(01.DESCR)->[Salle d'Attente]-(PART_OF)->[Cabinet Médical: #c]",
    '[Médecin: #z‹]-(a_spécialité)->[Anesthésie]',
  ],
  pages: {
    ADMIN: { title: 'Informations administratives', sections: ['ADMIN'] },
    TRAITEMENTS: { title: 'Traitement habituel', sections: ['TRAITEMENTS'] },
    ANTECEDENTS: { title: 'Antécédents', sections: ['ANTECEDENTS'] },
    ATCD_MED: { title: 'Antécédents médicaux', sections: ['ATCD_MED'] },
    FEMME: { title: 'Chez les femmes', sections: ['FEMME'] },
    CAPACITE_FONCT: {
      title: 'Capacité fonctionnelle',
      sections: ['CAPACITE_FONCT'],
    },
    DENTAIRE: { title: 'État bucco-dentaire', sections: ['DENTAIRE'] },
    INFO_ANESTH: {
      title: "Information sur l'anesthésie",
      sections: ['INFO_ANESTH'],
    },
    CONCLUSION: { title: 'Conclusion', sections: ['RECAP'] },
  },
  options: ['ALERTES'],
  visitMotive: 'Consultation pré-op',
  visitMotivesSection: 'Intervention',
};

export const anesthesieVariants = {
  anesthesie: {
    name: 'Anesthésie',
    status: 'deprecated',
    description:
      "Questionnaire pour les consultations d'anesthésie. Couvre les allergies, les antécédents cardio-vasculaires, respiratoires, digestifs et neurologiques, les hospitalisations et anesthésies antérieures, les traitements actuels et le mode de vie.",
    speciality: 'ANESTHESIE',
    patientOnboarding: 'DEFAULT',
    patientOffboarding: 'DEFAULT',
    emailTemplate: 'MED_G_DEFAULT',
    reportTemplate: 'ANESTHESIA',
    initialQuestions: [],
    pages: {
      CONSULTATION: { title: 'Consultation', sections: ['CONTEXTE'] },
      ALLERGIES: { title: 'Allergies', sections: ['ALLERGIES'] },
      CARDIOVASCULAIRE: {
        title: 'Antécédents cardio-vasculaires',
        sections: ['CARDIO'],
      },
      RESPIRATOIRE: {
        title: 'Antécédents respiratoires',
        sections: ['RESPIRATOIRE'],
      },
      DIGESTIF: { title: 'Antécédents digestifs', sections: ['DIGESTIF'] },
      NEURO: { title: 'Antécédents neurologiques', sections: ['NEURO'] },
      AUTRE: { title: 'Autres antécédents', sections: ['AUTRES_ATCD'] },
      GYNECO: { title: 'Contraception', sections: ['GYNECO'] },
      HOSPITAL: {
        title: 'Hospitalisations antérieures',
        sections: ['HOSPITAL'],
      },
      ANESTHESIE: {
        title: 'Anesthésies antérieures',
        sections: ['ANESTHESIE'],
      },
      HEMORRAGIE: { title: 'Saignements', sections: ['SAIGNEMENT'] },
      SANG: { title: 'Transfusions', sections: ['SANG'] },
      TRAITEMENTS: { title: 'Traitements actuels', sections: ['TRAITEMENTS'] },
      MODE_VIE: { title: 'Mode de vie', sections: ['MODE_VIE'] },
      DIVERS: { title: "Compléments d'information", sections: ['DIVERS'] },
      CONCLUSION: { title: 'Conclusion', sections: ['RECAP'] },
    },
    initialAssertions: [
      "[Message: #‹]-(06.a_lieu_emission)->[Lieu]-(01.DESCR)->[Salle d'Attente]-(PART_OF)->[Cabinet Médical: #c]",
      '[Médecin: #z‹]-(a_spécialité)->[Anesthésie]',
    ],
    options: ['ALERTES'],
  },
  anesthesie_preop: anesthesiePreop,
  anesthesie_preop_victoire: {
    ...anesthesiePreop,
    name: 'Anesthésie pré-opératoire (Victoire)',
    // Variante propre à un seul établissement : à ne pas proposer en
    // sélection générale dans le back-office.
    status: 'restricted',
    description:
      "Questionnaire pré-opératoire d'anesthésie pour l'établissement Victoire. Identique au questionnaire pré-opératoire standard, à ceci près que l'anesthésiste et le praticien opérateur sont choisis dans les listes nominatives de l'établissement au lieu d'être saisis en texte libre.",
    // ANESTHESIE_VICTOIRE rallume dans la KB les deux questions à liste
    // nominative (#ANS_ANESTHESISTE, #ANS_PRATICIEN) et inhibe en retour
    // #ANS_ANESTHESISTE_LIBRE / #ANS_PRATICIEN_LIBRE, qui portent les mêmes
    // libellés en saisie TEXT (knowledgebase/src/anesthesie-preop/KB-ADMIN.mdkg).
    options: [...anesthesiePreop.options, 'ANESTHESIE_VICTOIRE'],
  },
  anesthesie_preop_cedre: {
    ...anesthesiePreop,
    name: 'Anesthésie pré-opératoire (Clinique du Cèdre)',
    // Variante propre à un seul établissement : à ne pas proposer en
    // sélection générale dans le back-office.
    status: 'restricted',
    description:
      "Questionnaire pré-opératoire d'anesthésie pour la Clinique du Cèdre. Identique au questionnaire pré-opératoire standard, avec en plus le port d'un pacemaker ou d'un défibrillateur implantable, l'appareillage de l'apnée du sommeil, et la transmission des dernières biologies et du dernier bilan cardiologique.",
    // ANESTHESIE_CEDRE allume dans la KB #ANS_CARDIO_DISPOSITIF
    // (KB-ATCD-MED.mdkg), #ANS_RESPI_SAOS_APPAREIL (KB-ATCD-MED.mdkg) et
    // #ANS_DOCS_PREOP (KB-INFO.mdkg) ; les (après) voisins sont forkés sur
    // le même marqueur (knowledgebase/src/anesthesie-preop/).
    options: [...anesthesiePreop.options, 'ANESTHESIE_CEDRE'],
  },
  anesthesie_preop_st_roch: {
    ...anesthesiePreop,
    name: 'Anesthésie pré-opératoire (Saint-Roch)',
    // Variante propre à un seul établissement : à ne pas proposer en
    // sélection générale dans le back-office.
    status: 'restricted',
    description:
      "Questionnaire pré-opératoire d'anesthésie pour l'établissement Saint-Roch. Identique au questionnaire pré-opératoire standard, à ceci près que l'anesthésiste et le praticien opérateur sont choisis dans les listes nominatives de l'établissement au lieu d'être saisis en texte libre.",
    // Même dispositif que Victoire : ANESTHESIE_ST_ROCH rallume dans la KB
    // #ANS_ANESTHESISTE_ST_ROCH / #ANS_PRATICIEN_ST_ROCH et inhibe en retour
    // #ANS_ANESTHESISTE_LIBRE / #ANS_PRATICIEN_LIBRE
    // (knowledgebase/src/anesthesie-preop/KB-ADMIN.mdkg).
    options: [...anesthesiePreop.options, 'ANESTHESIE_ST_ROCH'],
  },
} satisfies Variants;
